/**
 * Types and PURE helpers for /analytics/segment-analytics (task-7d6o29fdh7).
 *
 * One segment from GET /analytics/segments/:siteId/performance. Since step (a) every metric and
 * trend is `null` when nothing was recorded for it in the window: the endpoint no longer COALESCEs
 * an absent daily row to 0, so a 0 here IS a measured zero. Everything that renders or aggregates
 * these values goes through isRecorded() and never substitutes a number.
 */
export type Measured = number | null;

export interface SegmentPerformance {
  segmentId: string;
  segmentName: string;
  segmentSlug: string;
  description: string;
  dimension: string;
  measured?: boolean;
  measuredDays?: number;
  userCount: Measured;
  metrics: {
    totalUsers: Measured;
    conversionRate: Measured;
    avgOrderValue: Measured;
    totalRevenue: Measured;
    avgSessionDuration: Measured;
    bounceRate: Measured;
    repeatPurchaseRate: Measured;
    lifetimeValue: Measured;
  };
  trends: {
    conversionTrend: Measured;
    revenueTrend: Measured;
    userGrowth: Measured;
  };
  timeSeriesData?: any[];
}
export type MetricKey = keyof SegmentPerformance['metrics'];

export const isRecorded = (v: Measured | undefined): v is number => typeof v === 'number' && Number.isFinite(v);

/** Sum of every RECORDED totalRevenue — the denominator for any revenue share, regardless of what else a segment recorded. */
export function recordedRevenueTotal(segments: SegmentPerformance[]): number {
  return segments.reduce((sum, s) => sum + (isRecorded(s.metrics.totalRevenue) ? s.metrics.totalRevenue : 0), 0);
}

/**
 * A segment's share of recorded revenue, in percent, or null when it is undefined: the segment's
 * revenue was not recorded, or no recorded revenue exists to divide by. A recorded $0 against a
 * positive total is a defined 0% — a measurement, not an absence (codex on #6316).
 */
export function revenueShare(segment: SegmentPerformance, segments: SegmentPerformance[]): number | null {
  const revenue = segment.metrics.totalRevenue;
  if (!isRecorded(revenue)) return null;
  const total = recordedRevenueTotal(segments);
  if (total <= 0) return null;
  return (revenue / total) * 100;
}

export interface Insight { title: string; description: string; actions: string[] }

/**
 * Insights are written only about RECORDED figures, and each insight checks ONLY the fields its
 * own prose uses (codex on #6316: a shared filter both suppressed valid growth insights and shrank
 * the revenue denominator whenever an unrelated field was null).
 *
 * `segments` is expected in endpoint order (recorded revenue descending, unrecorded last).
 */
export function generateInsights(segments: SegmentPerformance[], _metric: string): Insight[] {
  const insights: Insight[] = [];
  const revenueTotal = recordedRevenueTotal(segments);

  // Star performer: needs conversion, AOV and revenue recorded; its share is over ALL recorded revenue.
  const star = segments.find(s =>
    isRecorded(s.metrics.conversionRate) && isRecorded(s.metrics.avgOrderValue) && isRecorded(s.metrics.totalRevenue));
  if (star && revenueTotal > 0) {
    const share = ((star.metrics.totalRevenue as number) / revenueTotal) * 100;
    insights.push({
      title: `${star.segmentName} is your star performer`,
      description: `With a ${star.metrics.conversionRate}% conversion rate and $${star.metrics.avgOrderValue} AOV, this segment generates ${share.toFixed(1)}% of recorded revenue.`,
      actions: ['Create targeted campaigns', 'Expand similar audiences', 'Increase budget allocation'],
    });
  }

  // Growth: needs only the recorded user-growth trend.
  const growth = segments.find(s => isRecorded(s.trends?.userGrowth) && s.trends.userGrowth > 10);
  if (growth) {
    insights.push({
      title: `${growth.segmentName} shows strong growth potential`,
      // Says only what was measured: user growth. No claim about other metrics (codex-reviewer-2 on #6316).
      description: `This segment grew ${(growth.trends.userGrowth as number).toFixed(1)}% in user count over the previous period. There's an opportunity to capitalize on this momentum.`,
      actions: ['Analyze growth drivers', 'Scale acquisition efforts', 'Optimize user experience'],
    });
  }

  // Underperformer: needs only a recorded conversion rate.
  const under = segments.find(s => isRecorded(s.metrics.conversionRate) && s.metrics.conversionRate < 2);
  if (under) {
    insights.push({
      title: `${under.segmentName} needs optimization`,
      description: `With only ${under.metrics.conversionRate}% conversion rate, this segment has significant room for improvement.`,
      actions: ['A/B test messaging', 'Review user journey', 'Implement personalization'],
    });
  }

  return insights;
}
